import { useState, useEffect, useCallback } from 'react';
import { Button, Form } from 'react-bootstrap';
import { AppModal } from '../components/AppModal';
import {
  getStaffPackages,
  createPackage,
  replacePackage,
  setPackageEnabled,
  reorderPackages,
  deletePackage,
} from '../services/PackageService';
import { updateSetting } from '../services/DonationStoreService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { FormFeedback } from '../components/FormFeedback';
import CoinImage from '../images/pcoin.webp';
import { StaffPageHeader } from '../components/StaffPageHeader';
import { CurrencyDollar } from 'react-bootstrap-icons';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCps(n) {
  return Math.round(n).toLocaleString();
}

const EMPTY_FORM = { coinCount: '', bonusPct: '', tagline: '', outlineColor: '' };

// ── Package preview card (mirrors player view) ────────────────────────────────

function PackagePreview({ pkg, cpPerCoin, onMoveUp, onMoveDown, onEdit, onToggle, onDelete, isFirst, isLast }) {
  const bonusCps = Math.round(pkg.coinCount * cpPerCoin * pkg.bonusPct / 100);
  const hasBonus = bonusCps > 0;

  const cardStyle = pkg.outlineColor
    ? { '--pkg-outline': pkg.outlineColor }
    : undefined;

  return (
    <div className={`pkg-card pkg-card--staff${pkg.outlineColor ? ' pkg-card--highlighted' : ''}${!pkg.enabled ? ' pkg-card--disabled' : ''}`} style={cardStyle}>
      {pkg.tagline && <span className="pkg-tagline">{pkg.tagline}</span>}
      <div className="pkg-coin-row">
        <img src={CoinImage} alt="" className="pkg-coin-icon" />
        <span className="pkg-coin-count">{pkg.coinCount}</span>
        <span className="pkg-coin-label">Coins</span>
      </div>
      <p className="pkg-price">${pkg.coinCount}.00 <span className="pkg-price-currency">USD</span></p>
      {hasBonus && (
        <p className="pkg-bonus">+{formatCps(bonusCps)} bonus CPs</p>
      )}

      <div className="pkg-staff-controls">
        <div className="pkg-staff-reorder">
          <button className="pkg-ctrl-btn" onClick={onMoveUp} disabled={isFirst} aria-label="Move up">↑</button>
          <button className="pkg-ctrl-btn" onClick={onMoveDown} disabled={isLast} aria-label="Move down">↓</button>
        </div>
        <div className="pkg-staff-actions">
          <button className="pkg-ctrl-btn pkg-ctrl-btn--edit" onClick={onEdit}>Edit</button>
          <button
            className={`pkg-ctrl-btn ${pkg.enabled ? 'pkg-ctrl-btn--disable' : 'pkg-ctrl-btn--enable'}`}
            onClick={onToggle}
          >
            {pkg.enabled ? 'Disable' : 'Enable'}
          </button>
          <button className="pkg-ctrl-btn pkg-ctrl-btn--delete" onClick={onDelete}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Live preview used inside the modal ───────────────────────────────────────

function ModalPreview({ form, cpPerCoin }) {
  const coinCount = parseInt(form.coinCount, 10) || 0;
  const bonusPct = parseFloat(form.bonusPct) || 0;
  const bonusCps = Math.round(coinCount * cpPerCoin * bonusPct / 100);

  if (coinCount <= 0) return null;

  const cardStyle = form.outlineColor ? { '--pkg-outline': form.outlineColor } : undefined;

  return (
    <div className="pkg-modal-preview">
      <p className="pkg-modal-preview-label">Preview</p>
      <div className={`pkg-card pkg-card--preview${form.outlineColor ? ' pkg-card--highlighted' : ''}`} style={cardStyle}>
        {form.tagline && <span className="pkg-tagline">{form.tagline}</span>}
        <div className="pkg-coin-row">
          <img src={CoinImage} alt="" className="pkg-coin-icon" />
          <span className="pkg-coin-count">{coinCount}</span>
          <span className="pkg-coin-label">Coins</span>
        </div>
        <p className="pkg-price">${coinCount}.00 <span className="pkg-price-currency">USD</span></p>
        {bonusCps > 0 && (
          <p className="pkg-bonus">+{formatCps(bonusCps)} bonus CPs</p>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function StaffPackageManagement() {
  const [packages, setPackages] = useState([]);
  const [cpPerCoin, setCpPerCoin] = useState(150_000_000);
  const [cpInput, setCpInput] = useState('150000000');
  const [cpSaving, setCpSaving] = useState(false);
  const [cpFeedback, setCpFeedback] = useState(null); // null | { type, message }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Create / edit modal
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = create mode
  const [modalForm, setModalForm] = useState(EMPTY_FORM);
  const [modalError, setModalError] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);

  const loadPackages = useCallback(async () => {
    try {
      const data = await getStaffPackages();
      setPackages(data.packages);
      setCpPerCoin(data.cpPerCoin);
      setCpInput(String(data.cpPerCoin));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getStaffPackages();
        if (!cancelled) {
          setPackages(data.packages);
          setCpPerCoin(data.cpPerCoin);
          setCpInput(String(data.cpPerCoin));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── cp_per_coin setting ───────────────────────────────────────────────────

  async function saveCpPerCoin() {
    const val = parseInt(cpInput, 10);
    if (!val || val <= 0) {
      setCpFeedback({ type: 'error', message: 'Must be a positive integer.' });
      return;
    }
    setCpSaving(true);
    setCpFeedback(null);
    try {
      await updateSetting('cp_per_coin', String(val));
      setCpPerCoin(val);
      setCpFeedback({ type: 'success', message: 'Saved.' });
    } catch {
      setCpFeedback({ type: 'error', message: 'Failed to save.' });
    } finally {
      setCpSaving(false);
    }
  }

  // ── Reorder ───────────────────────────────────────────────────────────────

  async function movePackage(index, direction) {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= packages.length) return;
    const next = [...packages];
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setPackages(next); // optimistic
    try {
      await reorderPackages(next.map(p => p.id));
    } catch {
      await loadPackages(); // revert on failure
    }
  }

  // ── Enable / disable ──────────────────────────────────────────────────────

  async function handleToggle(pkg) {
    const next = [...packages];
    const idx = next.findIndex(p => p.id === pkg.id);
    next[idx] = { ...next[idx], enabled: !pkg.enabled };
    setPackages(next); // optimistic
    try {
      await setPackageEnabled(pkg.id, !pkg.enabled);
    } catch {
      await loadPackages(); // revert
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  async function handleDelete(id) {
    try {
      await deletePackage(id);
      setDeleteConfirmId(null);
      await loadPackages();
    } catch { /* ignore */ }
  }

  // ── Modal ─────────────────────────────────────────────────────────────────

  function openCreateModal() {
    setEditTarget(null);
    setModalForm({ ...EMPTY_FORM, sortOrder: packages.length });
    setModalError(null);
    setShowModal(true);
  }

  function openEditModal(pkg) {
    setEditTarget(pkg);
    setModalForm({
      coinCount: String(pkg.coinCount),
      bonusPct: String(pkg.bonusPct),
      tagline: pkg.tagline ?? '',
      outlineColor: pkg.outlineColor ?? '',
    });
    setModalError(null);
    setShowModal(true);
  }

  async function handleModalSave() {
    const coinCount = parseInt(modalForm.coinCount, 10);
    const bonusPct = parseFloat(modalForm.bonusPct);
    if (!coinCount || coinCount <= 0) {
      setModalError('Coin count must be a positive integer.');
      return;
    }
    if (isNaN(bonusPct) || bonusPct < 0 || bonusPct > 100) {
      setModalError('Bonus % must be between 0 and 100.');
      return;
    }
    setModalSaving(true);
    setModalError(null);
    const tagline = modalForm.tagline.trim() || null;
    const outlineColor = modalForm.outlineColor.trim() || null;
    try {
      if (editTarget) {
        await replacePackage(editTarget.id, coinCount, bonusPct, tagline, outlineColor);
      } else {
        await createPackage(coinCount, bonusPct, tagline, outlineColor, packages.length);
      }
      setShowModal(false);
      await loadPackages();
    } catch {
      setModalError('Failed to save package.');
    } finally {
      setModalSaving(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="spm-error">Failed to load packages.</p>;

  return (
    <div className="spm-page">
      <StaffPageHeader icon={<CurrencyDollar />} title="Coin Packages" />

      {/* ── Global config ─────────────────────────────────────────────── */}
      <section className="spm-section">
        <h2 className="spm-section-title">Coin → CP Rate</h2>
        <p className="spm-section-desc">How many Conquer Points 1 coin is worth (used to compute bonus CP display).</p>
        <div className="spm-cp-row">
          <Form.Control
            className="spm-cp-input"
            type="number"
            min="1"
            value={cpInput}
            onChange={e => setCpInput(e.target.value)}
          />
          <span className="spm-cp-label">CPs per coin</span>
          <Button className="spm-cp-save" onClick={saveCpPerCoin} disabled={cpSaving}>
            {cpSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
        {cpFeedback && (
          <FormFeedback type={cpFeedback.type} message={cpFeedback.message} />
        )}
      </section>

      {/* ── Package grid ──────────────────────────────────────────────── */}
      <section className="spm-section">
        <div className="spm-packages-header">
          <h2 className="spm-section-title">Packages</h2>
          <Button className="spm-btn-add" onClick={openCreateModal}>Add Package</Button>
        </div>

        {packages.length === 0
          ? <EmptyState ctaLabel="Add Package" ctaTo={null}>No packages configured yet.</EmptyState>
          : (
            <div className="pkg-grid pkg-grid--staff">
              {packages.map((pkg, idx) => (
                <PackagePreview
                  key={pkg.id}
                  pkg={pkg}
                  cpPerCoin={cpPerCoin}
                  isFirst={idx === 0}
                  isLast={idx === packages.length - 1}
                  onMoveUp={() => movePackage(idx, -1)}
                  onMoveDown={() => movePackage(idx, 1)}
                  onEdit={() => openEditModal(pkg)}
                  onToggle={() => handleToggle(pkg)}
                  onDelete={() => setDeleteConfirmId(pkg.id)}
                />
              ))}
            </div>
          )
        }
      </section>

      {/* ── Delete confirmation ────────────────────────────────────────── */}
      {deleteConfirmId !== null && (
        <div className="spm-delete-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="spm-delete-confirm" onClick={e => e.stopPropagation()}>
            <p>Delete this package? This cannot be undone.</p>
            <div className="spm-delete-actions">
              <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => handleDelete(deleteConfirmId)}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / edit modal ────────────────────────────────────────── */}
      <AppModal show={showModal} onHide={() => setShowModal(false)}>
        <AppModal.Header closeButton>
          <AppModal.Title>{editTarget ? 'Edit Package' : 'Add Package'}</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          <Form className="modal-form">
            <Form.Group className="spm-field mb-3">
              <Form.Label>Coin Count</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={modalForm.coinCount}
                onChange={e => setModalForm(f => ({ ...f, coinCount: e.target.value }))}
                placeholder="e.g. 8"
              />
            </Form.Group>
            <Form.Group className="spm-field mb-3">
              <Form.Label>Bonus CP %</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                step="0.0001"
                value={modalForm.bonusPct}
                onChange={e => setModalForm(f => ({ ...f, bonusPct: e.target.value }))}
                placeholder="e.g. 3.77"
              />
            </Form.Group>
            <Form.Group className="spm-field mb-3">
              <Form.Label>Tagline <span className="spm-optional">(optional)</span></Form.Label>
              <Form.Control
                type="text"
                maxLength={64}
                value={modalForm.tagline}
                onChange={e => setModalForm(f => ({ ...f, tagline: e.target.value }))}
                placeholder="e.g. Most Popular"
              />
            </Form.Group>
            <Form.Group className="spm-field mb-3">
              <Form.Label>Outline Color <span className="spm-optional">(optional)</span></Form.Label>
              <div className="spm-color-row">
                <input
                  type="color"
                  className="spm-color-picker"
                  value={modalForm.outlineColor || '#38bdf8'}
                  onChange={e => setModalForm(f => ({ ...f, outlineColor: e.target.value }))}
                />
                <Form.Control
                  type="text"
                  maxLength={32}
                  value={modalForm.outlineColor}
                  onChange={e => setModalForm(f => ({ ...f, outlineColor: e.target.value }))}
                  placeholder="e.g. #ff6b00 or leave blank"
                />
                {modalForm.outlineColor && (
                  <button
                    type="button"
                    className="spm-color-clear"
                    onClick={() => setModalForm(f => ({ ...f, outlineColor: '' }))}
                    aria-label="Clear color"
                  >✕</button>
                )}
              </div>
            </Form.Group>

            <ModalPreview form={modalForm} cpPerCoin={cpPerCoin} />

            <FormFeedback type="error" message={modalError} />
            <div className="modal-form-actions">
              <Button variant="secondary" onClick={() => setShowModal(false)} disabled={modalSaving}>Cancel</Button>
              <Button variant="success" onClick={handleModalSave} disabled={modalSaving}>
                {modalSaving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </Form>
        </AppModal.Body>
      </AppModal>
    </div>
  );
}

export { StaffPackageManagement };
